import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "lovegenome_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1, personalization = null) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.lookup_key === product.lookup_key && (i.personalization || null) === (personalization || null)
      );
      if (existing) {
        return prev.map((i) =>
          i === existing ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          lookup_key: product.lookup_key,
          id: product.id,
          name: product.name,
          price: product.price,
          format: product.format,
          image: product.image,
          personalization: personalization || null,
          quantity: qty,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((lookup_key) => {
    setItems((prev) => prev.filter((i) => i.lookup_key !== lookup_key));
  }, []);

  const updateQty = useCallback((lookup_key, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => (i.lookup_key === lookup_key ? { ...i, quantity: qty } : i)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.reduce((n, i) => n + i.quantity, 0);
  const subtotal = items.reduce((n, i) => n + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clearCart, count, subtotal, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
